import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, LayoutTemplate, Send, TrendingUp, Plus, ArrowRight } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis } from 'recharts';
import { api } from '../lib/api';
import { Lead, UserCategory } from '../types';
import { CategoryBadge, Spinner } from '../components/UI';
import { useAuth } from '../hooks/useAuth';

const COLORS = ['#6c63ff','#25d366','#f4c542','#ff6b6b','#e879f9','#38bdf8','#fb923c'];

export default function DashboardPage(): React.ReactElement {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.getDashboard().then(setStats).catch(console.error).finally(() => setLoading(false)); }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  if (loading) return <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}><Spinner size={32} /></div>;

  const catData = stats?.leadsByCategory ? Object.entries(stats.leadsByCategory).map(([name, value]) => ({ name, value })) : [];
  const recentLeads: Lead[] = stats?.recentLeads || [];
  const categories: UserCategory[] = stats?.categories || [];
  const getCatColor = (n: string) => categories.find((c: UserCategory) => c.name === n)?.color;

  return (
    <div className="page fade-in">
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, marginBottom: 4 }}>{greeting}, {user?.displayName?.split(' ')[0] || 'there'} 👋</h1>
        <p style={{ color: 'var(--text2)', fontSize: 14 }}>Here's what's happening in your pipeline</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 28 }}>
        {[
          { label: 'Total Leads', value: stats?.totalLeads ?? 0, icon: Users, color: 'var(--accent)', sub: 'in pipeline' },
          { label: 'Templates', value: stats?.totalTemplates ?? 0, icon: LayoutTemplate, color: 'var(--gold)', sub: 'created' },
          { label: 'Categories', value: stats?.totalCategories ?? 0, icon: TrendingUp, color: 'var(--accent3)', sub: 'segments' },
          { label: 'Emails Sent', value: stats?.emailsSent ?? 0, icon: Send, color: '#e879f9', sub: 'all time' },
        ].map(({ label, value, icon: Icon, color, sub }) => (
          <div key={label} className="stat-card">
            <div style={{ width: 36, height: 36, borderRadius: 9, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}><Icon size={17} /></div>
            <div className="stat-value" style={{ marginTop: 10 }}>{value}</div>
            <div className="stat-label">{label}</div>
            <div className="stat-sub">{sub}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 20 }}>Leads by Category</div>
          {catData.length === 0 ? <div className="empty-state" style={{ padding: '30px 0' }}><p>Add leads to see breakdown</p></div> : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart><Pie data={catData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                  {catData.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie><Tooltip contentStyle={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }} /></PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                {catData.map(({ name }: any, i: number) => <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text2)' }}><div style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS[i % COLORS.length] }} />{name}</div>)}
              </div>
            </>
          )}
        </div>
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 20 }}>Leads per Category</div>
          {catData.length === 0 ? <div className="empty-state" style={{ padding: '30px 0' }}><p>No data yet</p></div> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={catData} barSize={28}>
                <XAxis dataKey="name" tick={{ fill: 'var(--text3)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--text3)', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }} cursor={{ fill: 'rgba(108,99,255,0.06)' }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>{catData.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Recent Leads</div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/app/leads')} style={{ gap: 5 }}>View all <ArrowRight size={13} /></button>
        </div>
        {recentLeads.length === 0 ? (
          <div className="empty-state" style={{ padding: '28px 0' }}>
            <Users size={28} /><p>No leads yet</p>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/app/leads')}><Plus size={13} /> Add first lead</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {recentLeads.map((lead: Lead) => (
              <div key={lead.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 14px', borderRadius: 9, cursor: 'pointer', transition: 'background 0.15s' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                onClick={() => navigate('/app/leads')}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: 'var(--surface3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontFamily: 'Syne', fontWeight: 700, fontSize: 15, flexShrink: 0 }}>{lead.company.charAt(0).toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{lead.company}</div><div style={{ fontSize: 12, color: 'var(--text2)' }}>{lead.email}</div></div>
                <CategoryBadge name={lead.category} color={getCatColor(lead.category)} />
                <div style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0 }}>{new Date(lead.createdAt).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
